import { useSelector } from 'react-redux'

import {
  selectAvailableDashboardGroups,
  selectAvailableDashboardGroupsIncludeDashboards,
  selectDashboardGroup,
  selectDashboardGroupFromDashboardId,
  selectDashboardGroupHasManyDashboards,
  selectDashboardGroupOwner,
  selectDashboardGroupsInGroupIncludeDashboards,
  selectDashboardGroupsLoading,
  selectDeletedDashboardGroups,
  selectDeletedDashboardGroupsLoading,
  selectFavoriteDashboardGroups,
  selectRecentDashboardGroups
} from 'redux/selectors/DashboardGroups'

import { ApplicationState } from 'redux/Stores/types'

/**
 * Get a specific dashboard group by ID with resolved dashboards and tasks.
 * Returns null if the group has no dashboards (considered broken).
 * @param id - Dashboard group ID
 * @returns TDashboardGroup | null
 */
export const useDashboardGroup = (id: string | null) => {
  return useSelector((state: ApplicationState) =>
    selectDashboardGroup(state, id)
  )
}

/**
 * Get dashboard groups that belong to a specific group.
 * NOTE: group.dashboards contains dashboard_group_ids (not dashboard ids).
 * @param groupId - Group ID
 * @returns Array of dashboard groups belonging to the group
 */
export const useDashboardGroupsInGroup = (groupId: string | null) => {
  return useSelector((state: ApplicationState) =>
    selectDashboardGroupsInGroupIncludeDashboards(state, groupId)
  )
}

/**
 * Get dashboard group owner (formatted account name).
 * @param dashboardGroupId - Dashboard group ID
 * @returns Parsed owner with id and formatted name, or null if not found
 */
export const useDashboardGroupOwner = (dashboardGroupId: string) => {
  return useSelector((state: ApplicationState) =>
    selectDashboardGroupOwner(state, dashboardGroupId)
  )
}

/**
 * Get favorite dashboard groups (sorted alphabetically).
 * @returns Array of favorite dashboard groups sorted by title
 */
export const useFavoriteDashboardGroups = () => {
  return useSelector(selectFavoriteDashboardGroups)
}

/**
 * Get recently visited dashboard groups (max 5, sorted by date desc).
 * @returns Array of recently visited dashboard groups (max 5)
 */
export const useRecentDashboardGroups = () => {
  return useSelector(selectRecentDashboardGroups)
}

/**
 * Get dashboard groups loading state.
 * Returns true if currently fetching and not yet fetched.
 * @returns boolean - true if loading
 */
export const useDashboardGroupsLoading = () => {
  return useSelector(selectDashboardGroupsLoading)
}

/**
 * Get available dashboard groups (owned by me or shared with me).
 * @returns Array of available dashboard groups sorted by title
 */
export const useAvailableDashboardGroups = () => {
  return useSelector(selectAvailableDashboardGroups)
}

/**
 * Get soft-deleted dashboard groups (trash).
 * @returns Array of deleted dashboard groups
 */
export const useDeletedDashboardGroups = () => {
  return useSelector(selectDeletedDashboardGroups)
}

/**
 * Get trash (deleted dashboard groups) loading state.
 * @returns boolean - true if loading
 */
export const useDeletedDashboardGroupsLoading = () => {
  return useSelector(selectDeletedDashboardGroupsLoading)
}

/**
 * Get available dashboard groups with resolved dashboards.
 * Each group includes dashboards array and firstDashboardId.
 * @returns Array of dashboard groups with dashboards
 */
export const useAvailableDashboardGroupsIncludeDashboards = () => {
  return useSelector(selectAvailableDashboardGroupsIncludeDashboards)
}

/**
 * Get dashboard group from dashboard ID.
 * Fetches the dashboard first, then looks up its dashboard_group_id.
 * @param dashboardId - Dashboard ID
 * @returns TDashboardGroup | null
 */
export const useDashboardGroupFromDashboardId = (
  dashboardId: string | null
) => {
  return useSelector((state: ApplicationState) =>
    selectDashboardGroupFromDashboardId(state, dashboardId)
  )
}

/**
 * Check if dashboard group has many dashboards (more than one).
 * @param id - Dashboard group ID
 * @returns boolean - true if dashboard group has more than one dashboard
 */
export const useDashboardGroupHasManyDashboards = (id: string | null) => {
  return useSelector((state: ApplicationState) =>
    selectDashboardGroupHasManyDashboards(state, id)
  )
}
